import { useDeepCompareEffect } from "@react-hookz/web";
import { useCallback, useRef, useState } from "react";
import { useSupabase } from "./use-supabase";

import type { Database } from "../../../__definitions__/database-definitions";
import type { SupabaseHandlerArg, SupabaseSchemaName, UseSupabaseLoaderState } from "./types";
import type { DependencyList } from "react";

export class WaitingError extends Error {
  constructor() {
    super("WaitingError");
    this.name = "WaitingError";
  }
}

/**
 * An Effect for loading data from Supabase with a reload handler; may redirect if not authenticated.
 */
export function useSupabaseLoader<T, SchemaName extends SupabaseSchemaName<Database> = "public">(
  callback: (arg0: SupabaseHandlerArg<SchemaName>) => Promise<T>,
  deps: DependencyList,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  schema: SchemaName = "public" as SchemaName,
): UseSupabaseLoaderState<T> & { reload: () => void } {
  const [reloadCount, setReloadCount] = useState(0);
  const [state, setState] = useState<UseSupabaseLoaderState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const latestRequestId = useRef(0);

  useSupabase(
    async (ctx) => {
      const requestId = ++latestRequestId.current;
      if (!ctx) return setState({ data: null, loading: true, error: null });
      if (!state.loading) return;
      if (reloadCount === 0) return;

      try {
        const data = await callback(ctx);
        if (!ctx.abortController?.signal.aborted && requestId === latestRequestId.current)
          setState({ data, loading: false, error: null });
      } catch (error) {
        if (!ctx.abortController?.signal.aborted && requestId === latestRequestId.current)
          if (!(error instanceof WaitingError))
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            setState({ data: null, loading: false, error: error as Error });
      }
    },
    [state.loading, reloadCount], // Do not add callback
    schema,
  );

  const reload = useCallback(() => {
    setState({ data: null, loading: true, error: null });
    setReloadCount((old) => old + 1);
  }, []);

  useDeepCompareEffect(reload, deps);

  return { ...state, reload };
}
